'use client';

import { motion, useInView, useMotionValue, useTransform, useScroll, useSpring } from 'framer-motion';
import { useRef, MouseEvent } from 'react';
import { Plug, Users, Target, TrendingUp } from 'lucide-react';

const easing = [0.22, 1, 0.36, 1] as const;

const differentiators = [
  {
    icon: Target,
    title: 'Focus assoluto su ciò che conta',
    description:
      'Non siamo generalisti. Lavoriamo con aziende, studi professionali e chi vuole trasformare il digitale in un vantaggio reale — non con chi cerca una pagina web e basta.',
    metric: '100%',
    metricLabel: 'orientati ai risultati',
  },
  {
    icon: Plug,
    title: 'Si integra con ciò che già hai',
    description:
      'Connettiamo ERP, CRM, API e workflow n8n già in uso. Non ti chiediamo di cambiare tutto: costruiamo attorno a ciò che funziona già — e lo potenziamo.',
    metric: '0',
    metricLabel: 'silos di dati',
  },
  {
    icon: Users,
    title: 'Un solo referente, dalla strategia al go-live',
    description:
      'Niente passaggi da un reparto all\'altro. Seguiamo il progetto dall\'analisi al rilascio con un interlocutore fisso, comunicazione diretta e nessuna sorpresa.',
    metric: '1',
    metricLabel: 'referente dedicato',
  },
  {
    icon: TrendingUp,
    title: 'Risultati misurabili, definiti prima di iniziare',
    description:
      'Ogni progetto parte da KPI chiari e verificabili. Sai esattamente cosa costruiamo, perché e come misurare il successo — prima ancora di scrivere una riga di codice.',
    metric: 'KPI',
    metricLabel: 'definiti a priori',
  },
];

function DiffCard({ item, index }: { item: (typeof differentiators)[0]; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10% 0px' });
  const Icon = item.icon;

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-60, 60], [6, -6]);
  const rotateY = useTransform(x, [-60, 60], [-6, 6]);

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.65, delay: index * 0.1, ease: easing }}
      style={{ perspective: 800 }}
    >
      <motion.div
        className="group relative h-full rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7 transition-colors duration-300 hover:border-primary/30 hover:bg-white/[0.05] cursor-default"
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        transition={{ type: 'spring', stiffness: 180, damping: 22 }}
      >
        {/* Hover glow */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background: 'radial-gradient(circle at 50% 0%, rgba(230,43,54,0.08) 0%, transparent 70%)',
          }}
        />

        {/* Top accent line */}
        <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

        <div className="relative" style={{ transform: 'translateZ(20px)' }}>
          {/* Metric */}
          <div className="mb-5 flex items-end gap-2">
            <span className="text-4xl font-black tracking-tight text-white/10 group-hover:text-white/20 transition-colors duration-500">
              {item.metric}
            </span>
            <span className="mb-1 text-xs font-medium text-gray-600 group-hover:text-gray-500 transition-colors">
              {item.metricLabel}
            </span>
          </div>

          <div className="mb-5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
            <Icon className="h-4 w-4 text-primary" />
          </div>

          <h3 className="text-base font-semibold text-white leading-snug">
            {item.title}
          </h3>
          <p className="mt-3 text-sm leading-6 text-gray-500 group-hover:text-gray-400 transition-colors duration-300">
            {item.description}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Parallax large background text on scroll
function ParallaxWord({ text }: { text: string }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const x = useSpring(useTransform(scrollYProgress, [0, 1], ['-8%', '8%']), { stiffness: 60, damping: 20 });

  return (
    <div ref={ref} className="overflow-hidden pointer-events-none select-none" aria-hidden>
      <motion.div
        style={{ x }}
        className="whitespace-nowrap text-[clamp(5rem,14vw,11rem)] font-black leading-none text-white/[0.025] tracking-tighter"
      >
        {text}
      </motion.div>
    </div>
  );
}

export default function WhySection() {
  const headingRef = useRef(null);
  const headingInView = useInView(headingRef, { once: true, margin: '-10% 0px' });

  return (
    <section className="relative w-full bg-black py-32 lg:py-40 overflow-hidden">
      {/* Parallax background words */}
      <div className="absolute inset-0 flex flex-col justify-center gap-2 -z-0 pointer-events-none">
        <ParallaxWord text="INTEGRAZIONE DESIGN AUTOMAZIONE" />
        <ParallaxWord text="VELOCITÀ RISULTATI VISIONE" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-[2fr_3fr] lg:gap-20 items-start">

          {/* Left: heading (sticky on desktop) */}
          <motion.div
            ref={headingRef}
            initial={{ opacity: 0, y: 36 }}
            animate={headingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75, ease: easing }}
            className="lg:sticky lg:top-32"
          >
            <h2 className="text-4xl font-bold tracking-tight text-white lg:text-5xl text-balance">
              Non un&apos;agenzia. Il tuo team digitale.
            </h2>
            <p className="mt-6 text-base leading-7 text-gray-400">
              Entriamo nel merito velocemente perché lavoriamo su un perimetro preciso: chi vuole trasformare il digitale in un vantaggio reale, misurabile, duraturo.
            </p>

            {/* Animated accent line */}
            <motion.div
              className="mt-8 h-px bg-gradient-to-r from-primary/60 to-transparent"
              initial={{ scaleX: 0, originX: 0 }}
              animate={headingInView ? { scaleX: 1 } : {}}
              transition={{ duration: 1, delay: 0.4, ease: easing }}
            />
          </motion.div>

          {/* Right: 2x2 cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {differentiators.map((item, index) => (
              <DiffCard key={item.title} item={item} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
